import React from "react";
import Joi from "joi-browser";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import { getOrganizationsByUser } from "../../common/services/auth/userAccountOrganizationService";
import { getOrganizations } from "../../common/services/auth/organizationService";
import { getCommodities } from "./../../common/services/products/commodityService";
import { getOrAddInstrument } from "./../../common/services/marketdata/instrumentService";
import {
  formatDate,
  normalizeExpiryDateTime,
} from "./../../common/services/utilities";
import {
  getProducts,
  getProductWithQualityAttributes,
} from "./../../common/services/products/productService";
import { getMarketsWithAttributes } from "./../../common/services/markets/marketService";
import { getActiveQualityAttributeValueIdsByMarket } from "./../../common/services/markets/qualityAttributeValueMarketAssignmentService";
import { getTermsByDate } from "../../common/services/markets/termService";
import { getVenues } from "../../common/services/markets/venueService";
import { getCurrentOrderReleaseLimits } from "../../common/services/tradingEngine/orderReleaseLimitService";
import Form from "./../../common/components/form/form";
import { withTranslation } from "react-i18next";
import _ from "lodash";

class OrderForm extends Form {
  skipNextMarketInitialization = false;

  skipNextProductInitialization = false;

  state = {
    data: {
      orderDirection: "",
      organizationId: "",
      commodityId: "",
      productId: "",
      marketId: "",
      termId: "",
      priceBasisId: "",
      currencyId: "",
      uomId: "",
      price: "",
      volume: "",
      fillOrKill: false,
      allOrNone: false,
      orderState: "Live",
      eventId: "",
    },
    organizations: [],
    instrumentId: "",
    instruments: [],
    commodities: [],
    products: [],
    markets: [],
    venues: [],
    terms: [],
    productWithQualityAttributes: {},
    qualityAttributeValues: [],
    orderReleaseLimits: [],
    loadedReleaseLimitOrganizationId: "",
    loadingReleaseLimitOrganizationId: "",
    errors: {},
    isLoading: true,
    isLoadingReleaseLimits: false,
    touchedFields: {},
    submitAttempted: false,
  };

  schema = {
    commodityId: Joi.string().required().label(this.props.t("Commodity")),
    productId: Joi.string().required().label(this.props.t("Product")),
    marketId: Joi.string().required().label(this.props.t("Market")),
    priceBasisId: Joi.string().required().label(this.props.t("PriceBasis")),
    termId: Joi.string().required().label(this.props.t("Term")),
    uomId: Joi.string().required(),
    currencyId: Joi.string().required(),
    orderDirection: Joi.string(),
    organizationId: Joi.string().required().label(this.props.t("Organization")),
    price: Joi.number().required().label(this.props.t("Price")),
    volume: Joi.number().required().label(this.props.t("Volume")),
    fillOrKill: Joi.boolean().label(this.props.t("FillOrKill")),
    allOrNone: Joi.boolean().label(this.props.t("AllOrNone")),
    orderState: Joi.string(),
    eventId: Joi.string(),
  };

  async getActiveOrganizations(tradeOnBehalf, userAccountId) {
    const organizations = tradeOnBehalf
      ? await getOrganizations()
      : await getOrganizationsByUser(userAccountId);

    return organizations.filter((organization) => organization.active);
  }

  getOrderReleaseLimitKey(marketId, priceBasisId) {
    if (!marketId || !priceBasisId) {
      return null;
    }

    return `${marketId}:${priceBasisId}`;
  }

  getActiveMarketControl() {
    return this.props.marketControl.filter((marketControl) => marketControl.active);
  }

  getAssociatedOrganizationIds() {
    return new Set(
      this.getActiveMarketControl().flatMap(
        (marketControl) => marketControl.organizationIds || []
      )
    );
  }

  getAssociatedOrganizationIdsForMarket(marketId) {
    return (
      this.getActiveMarketControl().find(
        (marketControl) => marketControl.marketId === marketId
      )?.organizationIds || []
    );
  }

  shouldApplyIndividualOrderReleaseLimits() {
    return !this.props.tradeOnBehalf;
  }

  isOrderReleaseLimitsLoadedForOrganization(
    organizationId = this.state.data.organizationId
  ) {
    return !!organizationId &&
      this.state.loadedReleaseLimitOrganizationId === organizationId;
  }

  isOrderReleaseLimitLoadPending(
    organizationId = this.state.data.organizationId
  ) {
    return !!organizationId &&
      this.state.isLoadingReleaseLimits &&
      this.state.loadingReleaseLimitOrganizationId === organizationId;
  }

  getLoadedOrderReleaseLimitMarketIds(
    organizationId = this.state.data.organizationId
  ) {
    if (!this.isOrderReleaseLimitsLoadedForOrganization(organizationId)) {
      return null;
    }

    return new Set(
      (this.state.orderReleaseLimits || []).map((limit) => limit.marketId)
    );
  }

  getLoadedOrderReleaseLimitPriceBasisIds(
    marketId = this.state.data.marketId,
    organizationId = this.state.data.organizationId
  ) {
    if (!marketId || !this.isOrderReleaseLimitsLoadedForOrganization(organizationId)) {
      return null;
    }

    return new Set(
      (this.state.orderReleaseLimits || [])
        .filter((limit) => limit.marketId === marketId)
        .map((limit) => limit.priceBasisId)
    );
  }

  getCurrentOrderReleaseLimit(
    marketId = this.state.data.marketId,
    priceBasisId = this.state.data.priceBasisId
  ) {
    const key = this.getOrderReleaseLimitKey(marketId, priceBasisId);
    if (!key || !this.isOrderReleaseLimitsLoadedForOrganization()) {
      return null;
    }

    return (
      this.state.orderReleaseLimits.find(
        (limit) =>
          limit.marketId === marketId &&
          limit.priceBasisId === priceBasisId
      ) || {
        marketId,
        priceBasisId,
        maxVolume: 0,
        maxTotalValue: 0,
        priceVariancePercentage: null,
        priceVarianceAbsolute: null,
      }
    );
  }

  getFilteredOrganizations(data = this.state.data) {
    if (!data.marketId) {
      return { organizations: this.state.organizations };
    }

    const associatedOrganizationIds = new Set(
      this.getAssociatedOrganizationIdsForMarket(data.marketId)
    );

    return {
      organizations: this.state.organizations.filter((organization) =>
        associatedOrganizationIds.has(organization.organizationId)
      ),
    };
  }

  getAvailableMarketsByOrganization(
    organizationId = this.state.data.organizationId
  ) {
    const allowedMarketIds = new Set(
      this.getActiveMarketControl()
        .filter(
          (marketControl) =>
            !organizationId ||
            (marketControl.organizationIds || []).includes(organizationId)
        )
        .map((marketControl) => marketControl.marketId)
    );

    const markets = this.state.markets.filter((market) =>
      allowedMarketIds.has(market.marketId)
    );

    if (!this.shouldApplyIndividualOrderReleaseLimits()) {
      return markets;
    }

    const limitedMarketIds = this.getLoadedOrderReleaseLimitMarketIds(organizationId);
    if (!limitedMarketIds) {
      return markets;
    }

    return markets.filter((market) => limitedMarketIds.has(market.marketId));
  }

  getFilteredCommodities(data = this.state.data) {
    const marketProductIds = new Set(
      this.getAvailableMarketsByOrganization(data.organizationId).map(
        (market) => market.productId
      )
    );

    return {
      commodities: this.state.commodities.filter((commodity) =>
        this.state.products.some(
          (product) =>
            marketProductIds.has(product.productId) &&
            product.commodityId === commodity.commodityId
        )
      ),
    };
  }

  getFilteredProducts(data = this.state.data) {
    const marketProductIds = new Set(
      this.getAvailableMarketsByOrganization(data.organizationId).map(
        (market) => market.productId
      )
    );

    return {
      products: this.state.products.filter(
        (product) =>
          marketProductIds.has(product.productId) &&
          (!data.commodityId || product.commodityId === data.commodityId)
      ),
    };
  }

  getFilteredMarkets(data = this.state.data) {
    return {
      markets: this.getAvailableMarketsByOrganization(data.organizationId).filter(
        (market) => !data.productId || market.productId === data.productId
      ),
    };
  }

  getCoercedSelectionData(data = this.state.data) {
    const nextData = { ...data };

    const { commodities } = this.getFilteredCommodities(nextData);
    if (
      nextData.commodityId &&
      !commodities.some((commodity) => commodity.commodityId === nextData.commodityId)
    ) {
      nextData.commodityId = "";
      nextData.productId = "";
      nextData.marketId = "";
      nextData.termId = "";
      nextData.priceBasisId = "";
      nextData.currencyId = "";
      nextData.uomId = "";
      return nextData;
    }

    const { products } = this.getFilteredProducts(nextData);
    if (
      nextData.productId &&
      !products.some((product) => product.productId === nextData.productId)
    ) {
      nextData.productId = "";
      nextData.marketId = "";
      nextData.termId = "";
      nextData.priceBasisId = "";
      nextData.currencyId = "";
      nextData.uomId = "";
      return nextData;
    }

    const { markets } = this.getFilteredMarkets(nextData);
    if (
      nextData.marketId &&
      !markets.some((market) => market.marketId === nextData.marketId)
    ) {
      nextData.marketId = "";
      nextData.termId = "";
      nextData.priceBasisId = "";
      nextData.currencyId = "";
      nextData.uomId = "";
      return nextData;
    }

    const { priceBases } = this.getFilteredPriceBases(nextData);
    if (
      nextData.priceBasisId &&
      !priceBases.some((priceBasis) => priceBasis.priceBasisId === nextData.priceBasisId)
    ) {
      nextData.priceBasisId = "";
      nextData.currencyId = "";
      nextData.uomId = "";
    }

    return nextData;
  }

  getInitialOrganizationId(marketId) {
    const { organizations } = this.state;
    if (!organizations.length) {
      return "";
    }

    if (!marketId) {
      return organizations[0].organizationId;
    }

    const { organizations: filteredOrganizations } = this.getFilteredOrganizations({
      marketId,
    });

    return filteredOrganizations[0]?.organizationId || organizations[0].organizationId;
  }

  loadOrderReleaseLimits = async (organizationId, options = {}) => {
    const { force = false } = options;

    if (!organizationId) {
      this.setState(
        {
          orderReleaseLimits: [],
          loadedReleaseLimitOrganizationId: "",
          loadingReleaseLimitOrganizationId: "",
          isLoadingReleaseLimits: false,
        },
        this.syncDisplayedErrors
      );
      return;
    }

    if (
      !force &&
      this.isOrderReleaseLimitsLoadedForOrganization(organizationId) &&
      !this.isOrderReleaseLimitLoadPending(organizationId)
    ) {
      this.syncDisplayedErrors();
      return;
    }

    this.setState({
      isLoadingReleaseLimits: true,
      loadingReleaseLimitOrganizationId: organizationId,
    });

    try {
      const orderReleaseLimits =
        (await getCurrentOrderReleaseLimits(organizationId)) || [];
      this.setState(
        {
          orderReleaseLimits,
          loadedReleaseLimitOrganizationId: organizationId,
          loadingReleaseLimitOrganizationId: "",
          isLoadingReleaseLimits: false,
        },
        this.applyOrderReleaseLimitState
      );
    } catch (error) {
      console.error("Failed to load current order release limits", error);
      toast.error(this.props.t("OrderReleaseLimitLoadFailed"));
      this.setState(
        {
          orderReleaseLimits: [],
          loadedReleaseLimitOrganizationId: organizationId,
          loadingReleaseLimitOrganizationId: "",
          isLoadingReleaseLimits: false,
        },
        this.applyOrderReleaseLimitState
      );
    }
  };

  applyOrderReleaseLimitState = () => {
    const nextData = this.getCoercedSelectionData(this.state.data);
    if (!_.isEqual(nextData, this.state.data)) {
      this.setState({ data: nextData }, this.syncDisplayedErrors);
      return;
    }

    this.syncDisplayedErrors();
  };

  syncDisplayedErrors = () => {
    const errors = this.getDisplayedErrors();
    if (!_.isEqual(errors, this.state.errors)) {
      this.setState({ errors });
    }
  };

  getMarketInitializationState = async (marketId) => {
    const market = this.state.markets.find(
      (candidate) => candidate.marketId === marketId
    );

    if (!market) {
      return { terms: [], qualityAttributeValues: [] };
    }

    const date = formatDate(new Date());
    const [terms, qualityAttributeValues] = await Promise.all([
      getTermsByDate(market.termScheduleId, date),
      getActiveQualityAttributeValueIdsByMarket(market.marketId, date),
    ]);

    return {
      terms: terms.sort((a, b) =>
        new Date(a.endDate) > new Date(b.endDate) ? 1 : -1
      ),
      qualityAttributeValues,
    };
  };

  updateProductSchema(previousProductWithQualityAttributes = {}, nextProductWithQualityAttributes = {}) {
    const nextSchema = { ...this.schema };

    (previousProductWithQualityAttributes?.qualityAttributes || []).forEach(
      (attribute) => {
        delete nextSchema[attribute.qualityAttributeId];
      }
    );

    (nextProductWithQualityAttributes?.qualityAttributes || [])
      .filter((attribute) => attribute.isPriceDriver)
      .forEach((attribute) => {
        nextSchema[attribute.qualityAttributeId] = Joi.string()
          .required()
          .label(attribute.qualityAttributeName);
      });

    this.schema = nextSchema;
  }

  getProductInitializationState = async (productId) => {
    if (!productId) {
      return {};
    }

    return getProductWithQualityAttributes(productId);
  };

  async componentDidMount() {
    const { marketControl, tradeOnBehalf, userAccountId } = this.props;
    const marketControlMarkets = new Set(
      marketControl.filter((market) => market.active).map((market) => market.marketId)
    );
    const associatedOrganizationIds = this.getAssociatedOrganizationIds();

    const [allOrganizations, allMarkets, allProducts, allCommodities, allVenues] =
      await Promise.all([
        this.getActiveOrganizations(tradeOnBehalf, userAccountId),
        getMarketsWithAttributes(),
        getProducts(),
        getCommodities(),
        getVenues(),
      ]);

    const organizations = allOrganizations.filter((organization) =>
      associatedOrganizationIds.has(organization.organizationId)
    );
    const markets = allMarkets.filter(
      (market) => market.active && marketControlMarkets.has(market.marketId)
    );

    const marketProductIds = new Set(markets.map((market) => market.productId));
    const products = allProducts.filter(
      (product) => product.active && marketProductIds.has(product.productId)
    );

    const marketCommodityIds = new Set(products.map((product) => product.commodityId));
    const commodities = allCommodities.filter(
      (commodity) =>
        commodity.active && marketCommodityIds.has(commodity.commodityId)
    );
    const venues = allVenues.filter((venue) => venue.active);

    this.setState(
      { organizations, commodities, products, markets, venues },
      () => {
        void this.initializeOrderForm();
      }
    );
  }

  async componentDidUpdate(previousProps, previousState) {
    if (
      !_.isEqual(previousProps.data, this.props.data) ||
      previousProps.type !== this.props.type ||
      previousProps.lastGridClick !== this.props.lastGridClick
    ) {
      this.setState({ isLoading: true }, () => {
        void this.initializeOrderForm();
      });
      return;
    }
    if (previousState.data.organizationId !== this.state.data.organizationId) {
      void this.loadOrderReleaseLimits(this.state.data.organizationId, { force: true });
    } else if (
      previousState.data.marketId !== this.state.data.marketId &&
      this.state.data.organizationId
    ) {
      void this.loadOrderReleaseLimits(this.state.data.organizationId, { force: true });
    }

    if (
      previousState.data.organizationId !== this.state.data.organizationId ||
      previousState.data.commodityId !== this.state.data.commodityId ||
      previousState.data.productId !== this.state.data.productId ||
      previousState.data.marketId !== this.state.data.marketId ||
      previousState.loadedReleaseLimitOrganizationId !==
        this.state.loadedReleaseLimitOrganizationId ||
      !_.isEqual(previousState.orderReleaseLimits, this.state.orderReleaseLimits)
    ) {
      const nextData = this.getCoercedSelectionData();
      if (!_.isEqual(nextData, this.state.data)) {
        this.setState({ data: nextData });
        return;
      }
    }

    if (previousState.data.marketId !== this.state.data.marketId) {
      if (this.skipNextMarketInitialization) {
        this.skipNextMarketInitialization = false;
      } else {
        const market = this.state.markets.find(
          (candidate) => candidate.marketId === this.state.data.marketId
        );
        const date = new Date();

        if (market) {
          const terms = await getTermsByDate(market.termScheduleId, formatDate(date));
          const qualityAttributeValues =
            await getActiveQualityAttributeValueIdsByMarket(
              market.marketId,
              formatDate(date)
            );
          this.setState({
            terms: terms.sort((a, b) =>
              new Date(a.endDate) > new Date(b.endDate) ? 1 : -1
            ),
            qualityAttributeValues,
          });
        } else {
          this.setState({ terms: [], qualityAttributeValues: [] });
        }

        this.setState({
          data: {
            ...this.state.data,
            priceBasisId: "",
            termId: "",
            currencyId: "",
            uomId: "",
          },
        });
      }
    }
    if (previousState.data.priceBasisId !== this.state.data.priceBasisId) {
      const { priceBases } = this.getFilteredPriceBases();
      const priceBasis = priceBases.find(
        (candidate) => candidate.priceBasisId === this.state.data.priceBasisId
      );
      this.setState({
        data: {
          ...this.state.data,
          uomId: priceBasis?.uomId ?? "",
          currencyId: priceBasis?.currencyId ?? "",
        },
      });
    }

    if (previousState.data.productId !== this.state.data.productId) {
      if (this.skipNextProductInitialization) {
        this.skipNextProductInitialization = false;
      } else {
        const data = { ...this.state.data };

        (previousState.productWithQualityAttributes?.qualityAttributes || []).forEach(
          (attribute) => {
            delete data[attribute.qualityAttributeId];
          }
        );

        this.setState({ data });

        if (this.state.data.productId) {
          const product = await getProductWithQualityAttributes(
            this.state.data.productId
          );
          this.updateProductSchema(previousState.productWithQualityAttributes, product);
          this.setState({ productWithQualityAttributes: product });
        } else {
          this.updateProductSchema(previousState.productWithQualityAttributes, {});
          this.setState({ productWithQualityAttributes: {} });
        }
      }
    }
    if (
      previousState.data.organizationId !== this.state.data.organizationId ||
      previousState.data.marketId !== this.state.data.marketId ||
      previousState.data.price !== this.state.data.price ||
      previousState.data.volume !== this.state.data.volume ||
      previousState.data.termId !== this.state.data.termId ||
      previousState.data.priceBasisId !== this.state.data.priceBasisId ||
      previousState.data.currencyId !== this.state.data.currencyId ||
      previousState.data.uomId !== this.state.data.uomId ||
      previousState.data.orderDirection !== this.state.data.orderDirection ||
      previousState.loadedReleaseLimitOrganizationId !==
        this.state.loadedReleaseLimitOrganizationId ||
      previousState.isLoadingReleaseLimits !== this.state.isLoadingReleaseLimits ||
      !_.isEqual(previousState.orderReleaseLimits, this.state.orderReleaseLimits) ||
      !_.isEqual(previousProps.marketTrades, this.props.marketTrades)
    ) {
      this.syncDisplayedErrors();
    }
  }

  initializeOrderForm = async () => {
    const { data, type, marketInstruments = [] } = this.props;
    const selectedInstrument =
      marketInstruments.find(
        (candidate) => candidate.instrumentId === data.instrumentId
      ) || {};
    const selectedQualityAttributeValues =
      (data.qualityAttributeValues && data.qualityAttributeValues.length
        ? data.qualityAttributeValues
        : selectedInstrument.qualityAttributeValues) || [];
    const selection = {
      instrumentId: data.instrumentId ?? selectedInstrument.instrumentId ?? "",
      marketId: data.marketId ?? selectedInstrument.marketId ?? "",
      termId: data.termId ?? selectedInstrument.termId ?? "",
      priceBasisId: data.priceBasisId ?? selectedInstrument.priceBasisId ?? "",
      currencyId: data.currencyId ?? selectedInstrument.currencyId ?? "",
      uomId: data.uomId ?? selectedInstrument.uomId ?? "",
      qualityAttributeValues: selectedQualityAttributeValues,
    };

    const organizationId = this.getInitialOrganizationId(selection.marketId);
    const market =
      this.getAvailableMarketsByOrganization(organizationId).find(
        (candidate) => candidate.marketId === selection.marketId
      ) ||
      this.state.markets.find(
        (candidate) => candidate.marketId === selection.marketId
      );
    const product = this.state.products.find(
      (candidate) => candidate.productId === (market ? market.productId : "")
    );
    const nextMarketId = market?.marketId || selection.marketId || "";
    const nextProductId = product?.productId || "";
    const [marketInitializationState, productWithQualityAttributes] =
      await Promise.all([
        this.getMarketInitializationState(nextMarketId),
        this.getProductInitializationState(nextProductId),
      ]);

    const isBidSelection = type === "Bid";
    const selectedPrice = isBidSelection ? data.bidPrice : data.offerPrice;
    const selectedVolume = isBidSelection
      ? data.runningTotalBidVolume ?? data.bidVolume
      : data.runningTotalOfferVolume ?? data.offerVolume;

    const order = this.getCoercedSelectionData({
      commodityId: product ? product.commodityId : "",
      productId: nextProductId,
      marketId: nextMarketId,
      termId: selection.termId,
      priceBasisId: selection.priceBasisId,
      currencyId: selection.currencyId,
      uomId: selection.uomId,
      orderDirection: type === "Bid" ? "Sell" : "Buy",
      organizationId,
      price: selectedPrice ?? "",
      volume: selectedVolume ?? "",
      orderState: "Live",
      fillOrKill: false,
      allOrNone: false,
      eventId: uuid(),
      ...selectedQualityAttributeValues.reduce((accumulator, attribute) => {
        accumulator[attribute.qualityAttributeId] = attribute.qualityAttributeValueId;
        return accumulator;
      }, {}),
    });

    this.skipNextMarketInitialization = this.state.data.marketId !== order.marketId;
    this.skipNextProductInitialization =
      this.state.data.productId !== order.productId;
    this.updateProductSchema(
      this.state.productWithQualityAttributes,
      productWithQualityAttributes
    );

    this.setState(
      {
        data: order,
        instrumentId: selection.instrumentId,
        terms: marketInitializationState.terms,
        qualityAttributeValues: marketInitializationState.qualityAttributeValues,
        productWithQualityAttributes,
        isLoading: false,
        errors: {},
        touchedFields: {},
        submitAttempted: false,
      },
      () => {
        void this.loadOrderReleaseLimits(order.organizationId, { force: true });
        this.syncDisplayedErrors();
      }
    );
  };
  formatNumber(value, precision = null) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return String(value);
    }

    if (precision !== null && precision !== undefined) {
      return numericValue.toFixed(precision);
    }

    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 6,
    }).format(numericValue);
  }

  formatCurrency(value, currencySymbol = "$", precision = 2) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return `${currencySymbol}${this.formatNumber(value, precision)}`;
  }
  setOrderDirection = (orderDirection) => {
    if (this.state.data.orderDirection === orderDirection) {
      return;
    }

    this.setState(
      {
        data: {
          ...this.state.data,
          orderDirection,
        },
      },
      this.syncDisplayedErrors
    );
  };

  handleChange = ({ currentTarget: input }) => {
    const data = { ...this.state.data };

    if (input.type === "checkbox") {
      data[input.name] = !data[input.name];
    } else {
      data[input.name] = input.value;
    }

    this.setState(
      {
        data,
        touchedFields: {
          ...this.state.touchedFields,
          [input.name]: true,
        },
      },
      this.syncDisplayedErrors
    );
  };
  getSelectedQualityAttributeValueIds(data = this.state.data) {
    return (this.state.productWithQualityAttributes?.qualityAttributes || [])
      .filter((attribute) => attribute.isPriceDriver)
      .map((attribute) => data[attribute.qualityAttributeId])
      .filter(Boolean)
      .map(String)
      .sort();
  }

  hasSameQualityAttributeSelection(left = [], right = []) {
    return left.length === right.length &&
      left.every((value, index) => value === right[index]);
  }

  getInstrumentQualityAttributeValueIds(instrument) {
    return (instrument?.qualityAttributeValues || [])
      .map((value) => String(value.qualityAttributeValueId))
      .sort();
  }
  findMatchingInstrument(data, qualityAttributeValues) {
    const selectedQualityAttributeValueIds = qualityAttributeValues
      .map((value) => String(value.qualityAttributeValueId))
      .sort();
    const candidateInstrumentId = this.state.instrumentId || data.instrumentId;
    const instruments = _.uniqBy(
      [
        ...(this.props.marketInstruments || []),
        ...(this.state.instruments || []),
      ].filter(Boolean),
      (instrument) => instrument.instrumentId
    );
    const matchesSelection = (candidate) =>
      candidate.marketId === data.marketId &&
      candidate.termId === data.termId &&
      candidate.priceBasisId === data.priceBasisId &&
      candidate.currencyId === data.currencyId &&
      candidate.uomId === data.uomId &&
      this.hasSameQualityAttributeSelection(
        this.getInstrumentQualityAttributeValueIds(candidate),
        selectedQualityAttributeValueIds
      );

    return instruments.find((candidate) => {
      if (candidateInstrumentId && candidate.instrumentId === candidateInstrumentId) {
        return matchesSelection(candidate);
      }

      return matchesSelection(candidate);
    }) || null;
  }

  getLatestTradeForSelection(data = this.state.data) {
    if (
      !data.marketId ||
      !data.termId ||
      !data.priceBasisId ||
      !data.currencyId ||
      !data.uomId
    ) {
      return null;
    }

    const selectedQualityAttributeValueIds = this.getSelectedQualityAttributeValueIds(data);

    return _.orderBy(
      (this.props.marketTrades || []).filter((trade) => {
        if (
          trade.marketId !== data.marketId ||
          trade.termId !== data.termId ||
          trade.priceBasisId !== data.priceBasisId ||
          trade.currencyId !== data.currencyId ||
          trade.uomId !== data.uomId
        ) {
          return false;
        }

        const tradeQualityAttributeValueIds = (trade.qualityAttributeValues || [])
          .map((qualityAttributeValue) =>
            String(qualityAttributeValue.qualityAttributeValueId)
          )
          .sort();

        return this.hasSameQualityAttributeSelection(
          selectedQualityAttributeValueIds,
          tradeQualityAttributeValueIds
        );
      }),
      ["tradeDate"],
      ["desc"]
    )[0] ?? null;
  }

  getFilteredPriceBases(data = this.state.data) {
    const market = this.state.markets.find(
      (candidate) => candidate.marketId === data.marketId
    );
    const priceBases = (market?.marketAttributes || []).filter(
      (marketAttribute) => marketAttribute.active !== false
    );

    if (!this.shouldApplyIndividualOrderReleaseLimits()) {
      return { priceBases };
    }

    const limitedPriceBasisIds = this.getLoadedOrderReleaseLimitPriceBasisIds(
      data.marketId,
      data.organizationId
    );

    if (!limitedPriceBasisIds) {
      return { priceBases };
    }

    return {
      priceBases: priceBases.filter((priceBasis) =>
        limitedPriceBasisIds.has(priceBasis.priceBasisId)
      ),
    };
  }

  getMarketAttributes(data = this.state.data) {
    const { priceBases } = this.getFilteredPriceBases(data);
    return {
      marketAttributes:
        priceBases.find((priceBasis) => priceBasis.priceBasisId === data.priceBasisId) ||
        {},
    };
  }

  getOrderReleaseLimitEvaluation(
    orderDirection = this.state.data.orderDirection,
    data = this.state.data
  ) {
    const limit = this.getCurrentOrderReleaseLimit(data.marketId, data.priceBasisId);
    const price = Number(data.price);
    const volume = Number(data.volume);
    const totalValue =
      Number.isFinite(price) && Number.isFinite(volume) ? price * volume : null;
    const { marketAttributes } = this.getMarketAttributes(data);
    const currencySymbol = marketAttributes.currencySymbol ?? "$";
    const pricePrecision = marketAttributes.pricePrecision ?? 2;
    const volumePrecision = marketAttributes.volumePrecision ?? 0;
    const lastTrade = this.getLatestTradeForSelection(data);

    const volumeLimitExceeded =
      limit?.maxVolume !== null &&
      limit?.maxVolume !== undefined &&
      Number.isFinite(volume) &&
      volume > limit.maxVolume;
    const totalValueLimitExceeded =
      limit?.maxTotalValue !== null &&
      limit?.maxTotalValue !== undefined &&
      totalValue !== null &&
      totalValue > limit.maxTotalValue;

    const volumeMessage = volumeLimitExceeded
      ? this.props.t("OrderReleaseLimitVolumeExceeded", {
          actual: this.formatNumber(volume, volumePrecision),
          limit: this.formatNumber(limit.maxVolume, volumePrecision),
        })
      : null;

    const totalValueMessage = totalValueLimitExceeded
      ? this.props.t("OrderReleaseLimitTotalValueExceeded", {
          actual: this.formatCurrency(totalValue, currencySymbol, pricePrecision),
          limit: this.formatCurrency(limit.maxTotalValue, currencySymbol, pricePrecision),
        })
      : null;

    let priceWarningMessage = null;
    let warningMessage = null;
    if (
      orderDirection &&
      lastTrade &&
      Number.isFinite(price) &&
      Number.isFinite(Number(lastTrade.price)) &&
      limit &&
      ((limit.priceVariancePercentage !== null &&
        limit.priceVariancePercentage !== undefined) ||
        (limit.priceVarianceAbsolute !== null &&
          limit.priceVarianceAbsolute !== undefined))
    ) {
      const lastTradePrice = Number(lastTrade.price);
      const absoluteVariance =
        orderDirection === "Buy" ? price - lastTradePrice : lastTradePrice - price;
      const warningReasons = [];

      if (
        absoluteVariance > 0 &&
        limit.priceVarianceAbsolute !== null &&
        limit.priceVarianceAbsolute !== undefined &&
        absoluteVariance > limit.priceVarianceAbsolute
      ) {
        warningReasons.push(
          this.props.t("OrderReleaseLimitPriceVarianceAbsoluteExceeded", {
            variance: this.formatCurrency(absoluteVariance, currencySymbol, pricePrecision),
            limit: this.formatCurrency(
              limit.priceVarianceAbsolute,
              currencySymbol,
              pricePrecision
            ),
          })
        );
      }

      if (
        absoluteVariance > 0 &&
        limit.priceVariancePercentage !== null &&
        limit.priceVariancePercentage !== undefined &&
        lastTradePrice !== 0
      ) {
        const percentageVariance =
          (Math.abs(absoluteVariance) / Math.abs(lastTradePrice)) * 100;
        if (percentageVariance > limit.priceVariancePercentage) {
          warningReasons.push(
            this.props.t("OrderReleaseLimitPriceVariancePercentageExceeded", {
              variance: this.formatNumber(percentageVariance, 2),
              limit: this.formatNumber(limit.priceVariancePercentage, 2),
            })
          );
        }
      }

      if (warningReasons.length > 0) {
        priceWarningMessage = warningReasons.join(" ");
        warningMessage = this.props.t("OrderReleaseLimitWarningConfirm", {
          price: this.formatCurrency(price, currencySymbol, pricePrecision),
          lastTradedPrice: this.formatCurrency(
            lastTradePrice,
            currencySymbol,
            pricePrecision
          ),
          direction:
            orderDirection === "Buy"
              ? this.props.t("Bid")
              : this.props.t("Offer"),
          details: priceWarningMessage,
        });
      }
    }

    return {
      limit,
      lastTrade,
      volumeMessage,
      totalValueMessage,
      priceWarningMessage,
      warningMessage,
      hasProhibition: !!volumeMessage || !!totalValueMessage,
      hasWarning: !!warningMessage,
      prohibitionMessages: [volumeMessage, totalValueMessage].filter(Boolean),
    };
  }

  getValidationErrors = (
    data = this.state.data,
    orderDirection = this.state.data.orderDirection
  ) => {
    const { error } = Joi.validate(data, this.schema, {
      abortEarly: false,
      allowUnknown: this.state.joiAllowUnknown ?? false,
    });
    const errors = {};

    if (error) {
      for (const item of error.details) {
        errors[item.path[0]] = item.message;
      }
    }

    const evaluation = this.getOrderReleaseLimitEvaluation(orderDirection, data);
    if (evaluation.volumeMessage) {
      errors.volume = evaluation.volumeMessage;
    }
    if (evaluation.totalValueMessage) {
      errors.orderReleaseTotalValue = evaluation.totalValueMessage;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  validate = () => this.getValidationErrors();

  getDisplayedErrors = (
    validationErrors = this.getValidationErrors(),
    touchedFields = this.state.touchedFields,
    submitAttempted = this.state.submitAttempted,
    orderDirection = this.state.data.orderDirection,
    data = this.state.data
  ) => {
    const errors = {};

    Object.entries(validationErrors || {}).forEach(([field, message]) => {
      if (field === "orderReleaseTotalValue") {
        return;
      }

      if (submitAttempted || touchedFields[field]) {
        errors[field] = message;
      }
    });

    const evaluation = this.getOrderReleaseLimitEvaluation(orderDirection, data);
    if (
      !errors.price &&
      evaluation.priceWarningMessage &&
      (submitAttempted || touchedFields.price)
    ) {
      errors.price = evaluation.priceWarningMessage;
    }

    return errors;
  };

  handleOrderSubmit = async (orderDirection) => {
    const data = { ...this.state.data, orderDirection };
    const validationErrors = this.getValidationErrors(data, orderDirection);
    const displayedErrors = this.getDisplayedErrors(
      validationErrors,
      this.state.touchedFields,
      true,
      orderDirection,
      data
    );

    this.setState({
      data,
      submitAttempted: true,
      errors: displayedErrors,
    });

    if (validationErrors) {
      const evaluation = this.getOrderReleaseLimitEvaluation(orderDirection, data);
      if (evaluation.hasProhibition) {
        toast.error(evaluation.prohibitionMessages.join(" "));
      }
      return;
    }

    if (this.isOrderReleaseLimitLoadPending(data.organizationId)) {
      return;
    }

    const evaluation = this.getOrderReleaseLimitEvaluation(orderDirection, data);
    if (evaluation.hasProhibition) {
      toast.error(evaluation.prohibitionMessages.join(" "));
      return;
    }

    if (evaluation.hasWarning && !window.confirm(evaluation.warningMessage)) {
      return;
    }

    await this.doSubmit(orderDirection, data);
  };
  doSubmit = async (orderDirection = this.state.data.orderDirection, data = this.state.data) => {
    const selectedMarket = this.state.markets.find(
      (market) => market.marketId === data.marketId
    );
    const { priceBases } = this.getFilteredPriceBases(data);
    const selectedPriceBasis = priceBases.find(
      (candidate) => candidate.priceBasisId === data.priceBasisId
    );
    const selectedTerm = this.state.terms.find(
      (term) => term.termId === data.termId
    );
    const selectedVenue = this.state.venues.find(
      (venue) => venue.venueId === selectedMarket?.venueId
    );

    if (!selectedMarket || !selectedTerm || !selectedPriceBasis || !selectedVenue) {
      toast.error("Selected market metadata is incomplete.");
      return;
    }

    const qualityAttributeValues =
      (this.state.productWithQualityAttributes.qualityAttributes || [])
        .filter((attribute) => attribute.isPriceDriver)
        .map((attribute) => {
          const value = this.state.productWithQualityAttributes.qualityAttributeValues.find(
            (candidate) =>
              candidate.qualityAttributeValueId === data[attribute.qualityAttributeId]
          );
          return {
            qualityAttributeId: attribute.qualityAttributeId,
            qualityAttributeValueId: data[attribute.qualityAttributeId],
            value: value?.value,
            active: value?.active,
          };
        });

    let instrument = this.findMatchingInstrument(data, qualityAttributeValues);
    const normalizedTermExpiryDateTime = normalizeExpiryDateTime(
      selectedTerm.expiryDate,
    );

    if (!instrument) {
      const instrumentInput = {
        marketId: data.marketId,
        marketName: selectedMarket.name,
        termId: data.termId,
        termName: selectedTerm.name,
        priceBasisId: data.priceBasisId,
        priceBasisName: selectedPriceBasis.priceBasisName,
        currencyId: data.currencyId,
        currencyName: selectedPriceBasis.currencyName,
        uomId: data.uomId,
        uomName: selectedPriceBasis.uomName,
        termExpiryDateTime: normalizedTermExpiryDateTime,
        venueTimeZone: selectedVenue.timeZone,
        active: true,
        eventId: data.eventId,
        qualityAttributeValues,
      };

      const response = await getOrAddInstrument(instrumentInput);
      instrument = {
        ...instrumentInput,
        instrumentId: response.instrumentId,
      };
      this.setState({ instruments: [...this.state.instruments, instrument] });
    }


    this.props.submitOrder({
      instrumentId: instrument.instrumentId,
      orderDirection,
      organizationId: data.organizationId,
      price: Number(data.price),
      volume: Number(data.volume),
      fillOrKill: data.fillOrKill,
      allOrNone: data.allOrNone,
      orderState: data.orderState,
      eventId: data.eventId,
      termExpiryDateTime: normalizedTermExpiryDateTime,
      venueTimeZone: selectedVenue.timeZone,
      MarketId: instrument.marketId,
      MarketName: instrument.marketName,
      TermId: instrument.termId,
      TermName: instrument.termName,
      PriceBasisId: instrument.priceBasisId,
      PriceBasisName: instrument.priceBasisName,
      CurrencyId: instrument.currencyId,
      CurrencyName: instrument.currencyName,
      UomId: instrument.uomId,
      UomName: instrument.uomName,
      currencySymbol: selectedPriceBasis.currencySymbol,
      pricePrecision: selectedPriceBasis.pricePrecision,
      volumePrecision: selectedPriceBasis.volumePrecision,
      QualityAttributeValues: instrument.qualityAttributeValues,
    });

    this.setState({
      data: {
        ...data,
        orderDirection,
        eventId: uuid(),
      },
    });
  };

  render() {
    const { data, terms, productWithQualityAttributes, qualityAttributeValues } =
      this.state;
    const { organizations } = this.getFilteredOrganizations();
    const { commodities } = this.getFilteredCommodities();
    const { products } = this.getFilteredProducts();
    const { markets } = this.getFilteredMarkets();
    const { priceBases } = this.getFilteredPriceBases();
    const { marketAttributes } = this.getMarketAttributes();
    const releaseLimitEvaluation = this.getOrderReleaseLimitEvaluation();
    const marketControlSettings = this.props.marketControl.find(
      (market) => market.marketId === data.marketId
    );
    const allowFillOrKill = marketControlSettings?.allowFillOrKill;
    const allowAllOrNone = marketControlSettings?.allowAllOrNone;
    const currencySymbol = marketAttributes.currencySymbol ?? "$";
    const pricePrecision = marketAttributes.pricePrecision ?? 2;
    const volumePrecision = marketAttributes.volumePrecision ?? 0;
    const priceString =
      data.price && currencySymbol + Number(data.price).toFixed(pricePrecision);
    const volumeString =
      data.volume && Number(data.volume).toFixed(volumePrecision);
    const isSubmitDisabled =
      !!this.validate() ||
      this.state.isLoading ||
      this.isOrderReleaseLimitLoadPending() ||
      !this.props.tradeAllowed;

    return (
      <div className="container-fluid">
        <form className="form-inline" onSubmit={(event) => event.preventDefault()}>
          <fieldset disabled={this.props.tradeAllowed ? "" : "disabled"}>
            <div className="row"><div className="col-sm-12">{this.renderSelect("organizationId", this.props.t("Organization"), organizations, "organizationId", "name")}</div></div>
            <div className="row"><div className="col-sm-12">{this.renderSelect("commodityId", this.props.t("Commodity"), commodities, "commodityId", "name")}</div></div>
            <div className="row"><div className="col-sm-12">{this.renderSelect("productId", this.props.t("Product"), products, "productId", "name")}</div></div>
            <div className="row"><div className="col-sm-12">{this.renderSelect("marketId", this.props.t("Market"), markets, "marketId", "name")}</div></div>
            {this.shouldApplyIndividualOrderReleaseLimits() &&
              this.isOrderReleaseLimitsLoadedForOrganization() &&
              markets.length === 0 &&
              data.organizationId && (
                <div className="alert alert-warning py-2">
                  {this.props.t("NoMarketsAvailableForTraderReleaseLimits")}
                </div>
              )}
            {(productWithQualityAttributes.qualityAttributes || [])
              .filter((attribute) => attribute.isPriceDriver)
              .map((attribute) => (
                <div className="row" key={attribute.qualityAttributeId}>
                  <div className="col">
                    {this.renderSelect(
                      attribute.qualityAttributeId,
                      attribute.qualityAttributeName,
                      productWithQualityAttributes.qualityAttributeValues.filter(
                        (value) =>
                          value.qualityAttributeId === attribute.qualityAttributeId &&
                          (value.active ||
                            value.qualityAttributeValueId ===
                              attribute.qualityAttributeId) &&
                          qualityAttributeValues.includes(value.qualityAttributeValueId)
                      ),
                      "qualityAttributeValueId",
                      "value"
                    )}
                  </div>
                </div>
              ))}
            <div className="row"><div className="col-sm-12">{this.renderSelect("termId", this.props.t("Term"), terms, "termId", "name")}</div></div>
            <div className="row"><div className="col-sm-12">{this.renderSelect("priceBasisId", this.props.t("PriceBasis"), priceBases, "priceBasisId", "priceBasisName")}</div></div>
            <div className="row">
              <div className="col-sm-6">{this.renderNumericInput("price", this.props.t("Price") + (marketAttributes.currencyName ? ` (${marketAttributes.currencyName})` : ""), pricePrecision, "number")}</div>
              <div className="col-sm-6">{this.renderNumericInput("volume", this.props.t("Volume") + (marketAttributes.uomName ? ` (${marketAttributes.uomName})` : ""), volumePrecision, "number")}</div>
            </div>

            {releaseLimitEvaluation.hasProhibition && (
              <div className="alert alert-danger py-2 mt-2 mb-2">
                {releaseLimitEvaluation.prohibitionMessages.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </div>
            )}
            <div className="row mt-3">
              <div className="col-sm-6" style={{ visibility: allowFillOrKill ? "visible" : "hidden" }}>{this.renderSwitch("fillOrKill", this.props.t("FillOrKill"))}</div>
              <div className="col-sm-6" style={{ visibility: allowAllOrNone ? "visible" : "hidden" }}>{this.renderSwitch("allOrNone", this.props.t("AllOrNone"))}</div>
            </div>
            <div className="row mt-3">
              <div className="col-sm-6" style={{ textAlign: "center" }}>
                <button type="button" disabled={isSubmitDisabled} onMouseEnter={() => this.setOrderDirection("Buy")} onClick={() => this.handleOrderSubmit("Buy")} className="btn btn-primary btn-trading-buy btn-lg">
                  {this.props.t("Buy")}<br /><h6>{volumeString && priceString && volumeString + " @ " + priceString}</h6>
                </button>
              </div>
              <div className="col-sm-6" style={{ textAlign: "center" }}>
                <button type="button" disabled={isSubmitDisabled} onMouseEnter={() => this.setOrderDirection("Sell")} onClick={() => this.handleOrderSubmit("Sell")} className="btn btn-danger btn-lg">
                  {this.props.t("Sell")}<br /><h6>{volumeString && priceString && volumeString + " @ " + priceString}</h6>
                </button>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
    );
  }
}

export default withTranslation(["trading"])(OrderForm);
















